import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import StoryCard from "../components/StoryCard";
import { Search, X, SlidersHorizontal, ChevronUp } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { INDUSTRIES, COMPANY_SIZES, CHALLENGE_CATEGORIES, OUTCOME_CATEGORIES, COUNTRIES, US_STATES } from "../lib/constants";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const FilterGroup = ({ label, value, options, onChange, testid }) => (
  <div data-testid={testid}>
    <div className="tc-eyebrow text-slate-500 mb-1.5">{label}</div>
    <div className="flex flex-wrap gap-1.5">
      <button
        onClick={() => onChange("")}
        className={`tc-pill border ${!value ? "bg-tc-ink text-white border-tc-ink" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}
      >All</button>
      {options.map((o) => (
        <button
          key={o}
          onClick={() => onChange(o)}
          className={`tc-pill border ${value === o ? "bg-tc-ink text-white border-tc-ink" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}
        >{o}</button>
      ))}
    </div>
  </div>
);

export default function Library() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [industry, setIndustry] = useState("");
  const [size, setSize] = useState("");
  const [challenge, setChallenge] = useState("");
  const [outcome, setOutcome] = useState("");
  const [consent, setConsent] = useState("");
  const [country, setCountry] = useState("");
  const [stateVal, setStateVal] = useState("");
  const [facilitator, setFacilitator] = useState("");
  const [facilitatorList, setFacilitatorList] = useState([]);
  const [company, setCompany] = useState("");
  const [companyList, setCompanyList] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    api.get("/stories/facilitators/list").then(({ data }) => setFacilitatorList(data || [])).catch(() => {});
    api.get("/stories/companies/list").then(({ data }) => setCompanyList(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (industry) params.industry = industry;
    if (size) params.company_size = size;
    if (challenge) params.challenge_type = challenge;
    if (outcome) params.outcome_category = outcome;
    if (consent) params.consent_status = consent;
    if (country) params.country = country;
    if (stateVal) params.state = stateVal;
    if (facilitator) params.facilitator = facilitator;
    if (company) params.company = company;
    const t = setTimeout(() => {
      api.get("/stories", { params }).then(({ data }) => { setStories(Array.isArray(data) ? data : []); setLoading(false); }).catch(() => { setStories([]); setLoading(false); });
    }, 250);
    return () => clearTimeout(t);
  }, [search, industry, size, challenge, outcome, consent, country, stateVal, facilitator, company]);

  const clear = () => { setSearch(""); setIndustry(""); setSize(""); setChallenge(""); setOutcome(""); setConsent(""); setCountry(""); setStateVal(""); setFacilitator(""); setCompany(""); };
  const anyFilter = !!(search || industry || size || challenge || outcome || consent || country || stateVal || facilitator || company);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-12">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="tc-eyebrow text-slate-400">The shared brain trust</div>
          <h1 className="font-display text-4xl sm:text-5xl text-tc-ink leading-tight">Library</h1>
          <p className="text-slate-500 mt-1 text-sm">Find a story that fits the call you're on right now.</p>
        </div>
        <div className="relative w-full sm:w-80">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search stories, outcomes, quotes…"
            data-testid="library-search-input"
            className="w-full h-12 pl-11 pr-4 rounded-2xl border-2 border-slate-200 focus:border-orange-500 outline-none text-sm"
          />
        </div>
      </div>

      {/* Collapsible filter section */}
      <div className="mt-5">
        <button
          onClick={() => setFiltersOpen((x) => !x)}
          data-testid="library-filters-toggle"
          className="w-full flex items-center justify-between px-5 py-3.5 rounded-2xl bg-white border border-slate-200 hover:border-slate-300 transition-all"
        >
          <span className="flex items-center gap-2 font-bold text-tc-ink text-sm">
            <SlidersHorizontal size={16} /> Filters
            {anyFilter && (
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-500 text-white text-[11px] font-extrabold">
                {[search, industry, size, challenge, outcome, consent, country, stateVal, facilitator, company].filter(Boolean).length}
              </span>
            )}
          </span>
          <motion.span animate={{ rotate: filtersOpen ? 0 : 180 }} transition={{ duration: 0.2 }}>
            <ChevronUp size={18} className="text-slate-400" />
          </motion.span>
        </button>

        <AnimatePresence initial={false}>
          {filtersOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <div className="tc-card p-5 mt-3 grid gap-4">
                <FilterGroup label="Industry" value={industry} options={INDUSTRIES} onChange={setIndustry} testid="filter-industry" />
                <FilterGroup label="Company size" value={size} options={COMPANY_SIZES} onChange={setSize} testid="filter-size" />
                <FilterGroup label="Challenge" value={challenge} options={CHALLENGE_CATEGORIES} onChange={setChallenge} testid="filter-challenge" />
                <FilterGroup label="Outcome" value={outcome} options={OUTCOME_CATEGORIES} onChange={setOutcome} testid="filter-outcome" />
                <FilterGroup label="Country" value={country} options={COUNTRIES} onChange={(v) => { setCountry(v); if (v !== "United States") setStateVal(""); }} testid="filter-country" />
                {country === "United States" && (
                  <FilterGroup label="State" value={stateVal} options={US_STATES} onChange={setStateVal} testid="filter-state" />
                )}
                <div data-testid="filter-company">
                  <div className="tc-eyebrow text-slate-500 mb-1.5">Company <span className="normal-case text-slate-400 font-normal">, type to filter</span></div>
                  <input
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="e.g. Cleveland Clinic"
                    list="company-suggestions"
                    className="w-full h-10 rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none px-3 text-sm"
                    data-testid="filter-company-input"
                  />
                  <datalist id="company-suggestions">
                    {companyList.map((c) => <option key={c} value={c} />)}
                  </datalist>
                </div>
                <div data-testid="filter-facilitator">
                  <div className="tc-eyebrow text-slate-500 mb-1.5">Facilitator <span className="normal-case text-slate-400 font-normal">, typos are fine</span></div>
                  <input
                    value={facilitator}
                    onChange={(e) => setFacilitator(e.target.value)}
                    placeholder="Start typing a name…"
                    list="facilitator-suggestions"
                    className="w-full h-10 rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none px-3 text-sm"
                    data-testid="filter-facilitator-input"
                  />
                  <datalist id="facilitator-suggestions">
                    {facilitatorList.map((f) => <option key={f} value={f} />)}
                  </datalist>
                </div>
                <FilterGroup label="Consent" value={consent} options={["approved_named", "approved_anonymized", "not_yet_asked"]} onChange={setConsent} testid="filter-consent" />
                {anyFilter && (
                  <button onClick={clear} className="self-start tc-pill border border-slate-300 hover:bg-slate-100" data-testid="library-clear-filters">
                    <X size={12} /> Clear all
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="text-slate-400 py-12 text-center">Loading…</div>
        ) : stories.length === 0 ? (
          <div className="tc-card p-10 text-center text-slate-500">
            <div className="font-display text-2xl text-tc-ink mb-1">No matches yet.</div>
            <div className="text-sm">Try clearing filters or capturing the first story in this category.</div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="library-grid">
            {stories.map((s) => (
              <StoryCard
                key={s.id}
                story={s}
                canEdit={isSuperAdmin}
                canDelete={isSuperAdmin}
                onEdit={(story) => navigate(`/capture/${story.id}`)}
                onDelete={async (story) => {
                  const label = story.source === "client"
                    ? `this testimonial from ${story.client_submitter_name || "the client"}`
                    : `"${(story.challenge_text || "this story").slice(0, 60)}"`;
                  if (!window.confirm(`Delete ${label}? This can't be undone.`)) return;
                  try {
                    await api.delete(`/stories/${story.id}`);
                    toast.success("Story deleted.");
                    setStories((cur) => cur.filter((x) => x.id !== story.id));
                  } catch (err) {
                    toast.error("Delete failed", { description: err?.response?.data?.detail || err?.message });
                  }
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
